/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import isEmail from 'validator/lib/isEmail';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { ActionContext } from 'js/lib/ActionContext';

import { Button, FullscreenDialog, Grid, Typography2, breakpoints } from '@coursera/cds-core';

import _t from 'i18n!nls/staff-management';

import withNotifications from '../common/notifications/withNotifications';
import type {
  EmailableInterface,
  StaffManagementStoreV2 as StaffManagementStoreClass,
  StaffMembershipInterface,
  StaffMembershipType,
} from '../store';
import { actions, selectors } from '../store';
import withErrorNotification from '../withErrorNotification';
import withFluxibleContextAsProps from '../withFluxibleContextAsProps';
import withShow from '../withShow';
import AddModalRolesCheckboxGroup from './AddModalRolesCheckboxGroup';
import SelectEmail from './SelectEmail';

const { Header, HeadingGroup, Content, Actions } = FullscreenDialog;

type Props = {
  /** Called when user clicks cancel button. */
  cancelOnClick?: () => void;
  /** If true, cancel button cannot be clicked (no callback). */
  cancelDisabled?: boolean;

  /** Name of the degree */
  degreeName?: string;

  /** Name of the partner institution */
  partnerName?: string;

  /** Callback to handle click, presecribed by CheckboxGroup. */
  onChange: (options: string[]) => void;

  /** Called when user clicks confirm button. */
  confirmOnClick?: () => void;
  /** If true, confirm button cannot be clicked (no callback). */
  confirmDisabled?: boolean;

  /** Id of entity to load data from. */
  fromId?: string;
  /** One of the types supported by the staff-membershp component. Modifies messaging shown. */
  type?: keyof typeof StaffMembershipType;

  /** Client side validation errors. */
  validationErrors?: Array<string>;

  /** Creates a new toast notification */
  notify: () => void;
};

const styles = {
  button: () => css`
    margin-right: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      flex-direction: column;
      margin: var(--cds-spacing-100) 0;
      width: 100%;
    }
  `,
  emailContainer: () => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: 40px;
  `,

  errors: () => css`
    color: var(--cds-color-red-700);
    margin: var(--cds-spacing-200) 0;
  `,
};

/**
 * Modal form displayed at the beginning of an add/invite flow.
 */
export const AddModal = ({
  cancelOnClick = _.noop,
  cancelDisabled = false,
  confirmOnClick = _.noop,
  confirmDisabled = false,
  onChange,
  fromId,
  degreeName,
  partnerName,
  type = 'partner' as const,
  validationErrors = [],
}: Props) => {
  let autoCompleteMessage = '';
  let title = '';
  let subheading = '';

  if (type === 'partner') {
    autoCompleteMessage = _t('1. Enter the emails of the staff members you want to add to this institution.');
    title = `Add Institution Staff Roles to ${partnerName}`;
    subheading = _t(
      // eslint-disable-next-line no-restricted-syntax
      'Institution staff roles provide access to content within your institution. If you want to provide program staff or course staff roles, you can do so in Settings for the respective program or course.'
    );
  } else if (type === 'program') {
    autoCompleteMessage = _t(
      '1. Enter the email address of the staff member you want to give 1 or more program staff roles. '
    );
    title = `Add Program Staff Roles for ${degreeName}`;
    subheading = _t(
      `Program staff roles provide access to content within a program. If you want to provide institution staff or course staff roles, you can do so in Settings for the respective institution or course.`
    );
  }

  return (
    // At time of writing modal does not allow pass through of additional properties. To allow selection of modal components
    // we wrap entire contents in `data-e2e` tag.
    <div data-e2e="staff-management-add-modal">
      <FullscreenDialog contentWidth="60%" open={true} onClose={cancelOnClick}>
        <Header />
        <div>
          <HeadingGroup heading={title} supportText={subheading} />
          <Content>
            <div
              css={() => css`
                margin-bottom: var(--cds-spacing-100);
              `}
            >
              <Typography2 component="h3" variant="subtitleMedium">
                {autoCompleteMessage}
              </Typography2>
            </div>
            <div>
              <Typography2 component="p" variant="bodySecondary">
                {_t(
                  'If you want to assign multiple staff to the same role(s), separate each email address with a comma.'
                )}
              </Typography2>
            </div>
            <div css={styles.emailContainer}>
              <SelectEmail />
            </div>
            {validationErrors?.length ? (
              <Typography2 component="p" css={styles.errors} color="error" variant="bodyPrimary">
                {validationErrors.map((message) => (
                  <span key={message}>{message} </span>
                ))}
              </Typography2>
            ) : null}
            <AddModalRolesCheckboxGroup action="add" fromId={fromId} type={type} onChange={onChange} />
          </Content>
          <Actions>
            <Grid container alignItems="center" justifyContent="flex-start">
              <div css={styles.button}>
                <Button data-e2e="btn-confirm" disabled={confirmDisabled} onClick={confirmOnClick} variant="primary">
                  {_t('Assign')}
                </Button>
              </div>
              <div css={styles.button}>
                <Button data-e2e="btn-cancel" disabled={cancelDisabled} onClick={cancelOnClick} variant="secondary">
                  {_t('Cancel')}
                </Button>
              </div>
            </Grid>
          </Actions>
        </div>
      </FullscreenDialog>
    </div>
  );
};

const validateUserEmails = (users: Array<EmailableInterface>): Array<string> => {
  const badUsers: Array<EmailableInterface> = _.filter(users, (user) => !isEmail(user.email));
  return _.map(badUsers, (user) => _t('#{email} is not a valid email.', user));
};

// While the BE allows the addition of new membership objects to users already considered
// staff, this is not supposed to be allowed. Modifications of user memberships are only supposed to
// happen through the edit or delete flows.
const validateUsersNotAlreadyStaff = (
  users: Array<EmailableInterface>,
  staff: { [email: string]: Array<StaffMembershipInterface> }
): Array<string> => {
  const usersAlreadyStaff: Array<EmailableInterface> = _.filter(users, (user) => !_.isEmpty(staff[user.email]));
  return _.map(usersAlreadyStaff, (user) => _t('#{email} is already a staff member and cannot be added.', user));
};

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction, notify }: $TSFixMe
) => {
  const state = StaffManagementStoreV2.get();
  const { fromId, type } = state;
  const show = state.ui?.addFlow?.show;
  const roles = state.ui?.addFlow?.roles;
  const users = state.ui?.addFlow?.users || [];
  const partnerRoleTemplatesState = StaffManagementStoreV2.partnerRoleTemplates();

  const staffLimitedByType = selectors.staffMemberships.getMembershipsByUserEmailLimitedByType(state);
  const validationErrors = _.concat(validateUserEmails(users), validateUsersNotAlreadyStaff(users, staffLimitedByType));

  return {
    confirmDisabled: !users?.length || !roles?.length || !!validationErrors.length,
    cancelOnClick: () => {
      executeAction(actions.addFlow.prompt, { show: false });
    },
    confirmOnClick: () => {
      executeAction(actions.addFlow.confirm, { fromId, type, roles, users, notify });
    },
    error: _.get(partnerRoleTemplatesState, 'error'),
    validationErrors,
    fromId,
    show,
    type,
  };
};

export default compose<Props, Pick<Props, 'partnerName' | 'degreeName'>>(
  withFluxibleContextAsProps(),
  withNotifications(),
  connectToStores<Props, Props & ActionContext>(['StaffManagementStoreV2'], storeConnector),
  withErrorNotification(),
  withShow()
)(AddModal);
